import type { CallablesMap } from "@aircap/common/callables";
import { FirebaseError, initializeApp, type FirebaseOptions } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
  type Auth,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// Playwright restores localStorage before page scripts. Vite HMR may re-evaluate this module.
let auth: Auth;
if (import.meta.env.MODE === "demo-test-e2e") {
  try {
    auth = initializeAuth(firebaseApp, { persistence: browserLocalPersistence });
  } catch (error) {
    if (!(error instanceof FirebaseError) || error.code !== "auth/already-initialized") {
      throw error;
    }
    auth = getAuth(firebaseApp);
  }
} else {
  auth = getAuth(firebaseApp);
}
export { auth };
auth.useDeviceLanguage();
// Only fictional phone numbers from the Firebase console can receive codes with this on.
if (import.meta.env.VITE_FIREBASE_PROJECT_ID === "aircap-dev")
  auth.settings.appVerificationDisabledForTesting = true;

const functions = getFunctions(firebaseApp, "europe-west1");
export const db = getFirestore(firebaseApp);

if (import.meta.env.DEV && !import.meta.env.VITE_FIREBASE_PROJECT_ID?.startsWith("demo-")) {
  // Sprite: functions emulator is on the Sprite, not the browser's 127.0.0.1.
  if (import.meta.env.VITE_FIREBASE_EMULATOR_SINGLE_ORIGIN === "true") {
    const { hostname, origin, port, protocol } = window.location;
    const emulatorPort = port ? parseInt(port, 10) : protocol === "https:" ? 443 : 80;
    connectFunctionsEmulator(functions, hostname, emulatorPort);
    (functions as unknown as { emulatorOrigin: string }).emulatorOrigin = origin;
  } else {
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  }
}
if (import.meta.env.VITE_FIREBASE_PROJECT_ID?.startsWith("demo-")) {
  const storage = getStorage(firebaseApp);

  // Sprite (and similar) expose one HTTPS origin. Proxy emulator traffic through
  // Vite on that origin and force https so the browser does not block mixed content.
  if (import.meta.env.VITE_FIREBASE_EMULATOR_SINGLE_ORIGIN === "true") {
    const { hostname, origin, port, protocol } = window.location;
    const emulatorPort = port ? parseInt(port, 10) : protocol === "https:" ? 443 : 80;

    connectAuthEmulator(auth, origin, { disableWarnings: true });
    connectFunctionsEmulator(functions, hostname, emulatorPort);
    connectFirestoreEmulator(db, hostname, emulatorPort);
    connectStorageEmulator(storage, hostname, emulatorPort);

    (functions as unknown as { emulatorOrigin: string }).emulatorOrigin = origin;

    const firestoreInternal = db as unknown as {
      _getSettings: () => { host: string; ssl: boolean };
      _setSettings: (settings: { host: string; ssl: boolean }) => void;
    };
    firestoreInternal._setSettings({
      ...firestoreInternal._getSettings(),
      host: `${hostname}:${emulatorPort}`,
      ssl: true,
    });

    const storageInternal = storage as unknown as { host: string; _protocol: string };
    storageInternal.host = `${hostname}:${emulatorPort}`;
    storageInternal._protocol = "https";
  } else {
    connectAuthEmulator(auth, "http://localhost:9099");
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectStorageEmulator(storage, "127.0.0.1", 9199);
  }
}

export function call<K extends keyof CallablesMap>(action: K) {
  type Params = CallablesMap[K]["params"];
  type Response = CallablesMap[K]["response"];

  const callable = httpsCallable<{ action: K; params: unknown }, Awaited<Response>>(
    functions,
    "callables",
  );

  return (params: Params) => {
    // httpsCallable encodes `undefined` as `null`, which optional zod params reject. JSON drops it.
    return callable({ action, params: JSON.parse(JSON.stringify(params)) }).then(
      (result) => result.data,
    );
  };
}
